import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, ChevronRight } from "lucide-react";
import { scrollToId, waLink, GENERAL_MESSAGE } from "@/lib/store";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { ProImage } from "./HeroPro";
import { EASE } from "./Reveal";

const LINES = ["Seu próximo", "iPhone está", "a um toque."];
const PRO_MODEL = "iPhone 18 Pro";

const MaskLine = ({ text, i }) => (
  <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
    <motion.span
      className="block"
      initial={{ y: "110%", rotate: 4 }}
      animate={{ y: "0%", rotate: 0 }}
      transition={{ duration: 1.2, delay: 0.15 + i * 0.13, ease: EASE }}
    >
      {i === 2 ? <span className="text-outline">{text}</span> : text}
    </motion.span>
  </span>
);

const ProLinks = () => {
  const order = () => {
    window.dispatchEvent(new CustomEvent("rc-order", { detail: { model: PRO_MODEL, category: "iphone" } }));
    scrollToId("pedido");
  };
  const buy = () => {
    window.dispatchEvent(new CustomEvent("rc-filter", { detail: "todos" }));
    scrollToId("estoque");
  };
  const link = "group inline-flex items-center gap-0.5 text-[15px] text-zinc-100 transition-colors hover:text-white";
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4, duration: 1 }}
      className="relative z-10 mt-3 flex items-center justify-center gap-6 whitespace-nowrap lg:pointer-events-auto lg:-mt-12">
      <button data-testid="hero-pro-order" onClick={order} className={link}>
        Encomendar <ChevronRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
      </button>
      <button data-testid="hero-pro-buy" onClick={buy} className={link}>
        Comprar <ChevronRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
      </button>
    </motion.div>
  );
};

export const Hero = ({ count, compact = false }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const proY = useTransform(scrollYProgress, [0, 1], [0, -220]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} data-testid="hero-section" className={`relative overflow-hidden pb-12 ${compact ? "pt-8 sm:pt-10" : "pt-40 lg:pt-32"}`}>
      <motion.div style={{ y: proY }} data-testid="hero-pro"
        className="relative mb-4 lg:pointer-events-none lg:absolute lg:-right-[12%] lg:top-[6%] lg:mb-0 lg:w-[78%]">
        <ProImage className="aspect-[16/5] w-full lg:aspect-[2000/700]" />
        <ProLinks />
      </motion.div>
      <div className="relative mx-auto grid max-w-[1440px] grid-cols-1 gap-12 px-5 sm:px-8 lg:pointer-events-none lg:grid-cols-12 lg:gap-8">
        <motion.div style={{ y: textY }} className="pointer-events-auto relative flex flex-col justify-between lg:col-span-6">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }} className="flex items-center gap-3">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#25D366] opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#25D366]" />
            </span>
            <span data-testid="hero-stock-count" className="mono text-[11px] sm:text-xs uppercase tracking-[0.28em] text-zinc-300">
              {count > 0 ? `${String(count).padStart(2, "0")} produtos disponíveis agora` : "Estoque atualizado"}
            </span>
          </motion.div>

          <h1 data-testid="hero-heading" className="font-display mt-6 text-[11vw] sm:text-6xl lg:text-[5.4rem] xl:text-[6.2rem] font-extrabold leading-[0.9] tracking-[-0.045em] text-white">
            {LINES.map((t, i) => <MaskLine key={t} text={t} i={i} />)}
          </h1>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 1, ease: EASE }} className="mt-8 max-w-xl">
            <p className="text-base sm:text-lg text-zinc-300 leading-relaxed">
              iPhones, MacBooks, iPads, Apple Watch e acessórios HPrime, com preço na vitrine e parcelamento em até 21x. Gostou de um? Um toque e você já está no nosso WhatsApp.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button data-testid="hero-cta-catalog" onClick={() => scrollToId("estoque")} className="group inline-flex items-center justify-center gap-3 rounded-full bg-white px-7 py-4 text-sm font-semibold text-black transition-transform duration-300 hover:scale-[1.03] active:scale-95">
                Ver aparelhos disponíveis
                <ArrowDownRight className="h-4 w-4 transition-transform duration-500 group-hover:rotate-[-45deg]" />
              </button>
              <a data-testid="hero-cta-whatsapp" href={waLink(GENERAL_MESSAGE)} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center justify-center gap-3 rounded-full border border-white/20 px-7 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:border-[#25D366] hover:bg-[#25D366]/10">
                <WhatsAppIcon className="h-4 w-4 text-[#25D366]" />
                Chamar no WhatsApp
              </a>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
